import { useCallback, useEffect, useRef, useState } from 'react';
import { useCallbackRef } from '../utils';

export interface UseIntervalOptions {
  /** If set, the interval will start automatically when the component is mounted, `false` by default */
  autoInvoke?: boolean;
}

export interface UseIntervalReturnValue {
  /** Starts the interval */
  start: () => void;

  /** Stops the interval */
  stop: () => void;

  /** Toggles the interval */
  toggle: () => void;

  /** Indicates if the interval is active */
  active: boolean;
}

export function useInterval(
  fn: () => void,
  interval: number,
  { autoInvoke = false }: UseIntervalOptions = {}
): UseIntervalReturnValue {
  const [active, setActive] = useState(false);
  const intervalRef = useRef<number | null>(null);
  const handleCallback = useCallbackRef(fn);
  const intervalValueRef = useRef(interval);
  intervalValueRef.current = interval;

  const start = useCallback(() => {
    setActive((old) => {
      if (!old && !intervalRef.current) {
        intervalRef.current = window.setInterval(handleCallback, intervalValueRef.current);
      }
      return true;
    });
  }, []);

  const stop = useCallback(() => {
    setActive(false);
    if (intervalRef.current) {
      window.clearInterval(intervalRef.current);
    }
    intervalRef.current = null;
  }, []);

  const toggle = useCallback(() => {
    setActive((current) => {
      if (current) {
        if (intervalRef.current) {
          window.clearInterval(intervalRef.current);
        }
        intervalRef.current = null;
        return false;
      }
      if (!intervalRef.current) {
        intervalRef.current = window.setInterval(handleCallback, intervalValueRef.current);
      }
      return true;
    });
  }, []);

  useEffect(() => {
    active && start();
    return stop;
  }, [active, interval]);

  useEffect(() => {
    if (autoInvoke) {
      start();
    }
  }, []);

  return { start, stop, toggle, active };
}

export namespace useInterval {
  export type Options = UseIntervalOptions;
  export type ReturnValue = UseIntervalReturnValue;
}
